import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
	makeStyles,
	Avatar,
	Badge,
	Backdrop,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	TextField,
} from '@material-ui/core';
import { Cancel } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import Dropzone from 'react-dropzone';

import { ConfirmDialog } from './../../common/components';
import { userAvatarState, userNameState } from './../../common/recoil/UserInfo/atoms';
import {
	deleteAvatar,
	getUserAvatars,
	uploadAvatar,
} from './../../common/services/avatarsRequests';

const useStyles = makeStyles((theme) => ({
	close: {
		margin: 0,
		padding: 0,
	},
	avatar: {
		cursor: 'pointer',

		width: theme.spacing(7),
		height: theme.spacing(7),
	},
	selectedAvatar: {
		border: '1px solid red',
	},
	backdrop: {
		zIndex: theme.zIndex.drawer + 1,
		color: '#fff',
	},
}));

const StyledBadge = withStyles((theme) => ({
	badge: {
		right: 4,
		top: 8,
		border: `0px solid ${theme.palette.background.paper}`,
		padding: '0',
	},
}))(Badge);

const UserInfoSelector = ({ open, setOpen }) => {
	const classes = useStyles();
	const { t, i18n } = useTranslation();
	const [name, setName] = useRecoilState(userNameState);
	const [selectedAvatar, setSelectedAvatar] = useRecoilState(userAvatarState);
	const [avatars, setAvatars] = useState([]);
	const [loading, setLoading] = useState(false);
	const [avatarToDelete, setAvatarToDelete] = useState();
	const [showDeleteAvatar, setShowDeleteAvatar] = useState(false);
	let nameInput = useRef();

	const handleSave = () => {
		if (nameInput.current.value !== '') {
			setName(nameInput.current.value);
			setOpen(false);
		}
	};

	const fetchAvatars = async () => {
		setLoading(true);
		const response = await getUserAvatars();
		setAvatars(response.avatars);
		if (response?.avatars?.length === 1) {
			setSelectedAvatar(response?.avatars[0]);
		}
		setLoading(false);
	};

	const deleteAvatarById = async () => {
		setLoading(true);
		setShowDeleteAvatar(false);
		await deleteAvatar(avatarToDelete);
		await fetchAvatars();
		setLoading(false);
	};

	const onDrop = useCallback(async (acceptedFiles) => {
		acceptedFiles.forEach(async (file) => {
			const reader = new FileReader();

			reader.onabort = () => console.log('file reading was aborted');
			reader.onerror = () => console.log('file reading has failed');
			reader.onload = async () => {
				const binaryStr = reader.result;
				try {
					setLoading(true);
					const newFile = await uploadAvatar(binaryStr, file);
					await fetchAvatars();
					setLoading(false);
				} catch (e) {
					console.log('error', e);
					//setUploadingFile(false);
				}
			};
			reader.readAsDataURL(file);
		});
	}, []);

	useEffect(() => {
		if (open) {
			fetchAvatars();
		}
	}, [open]);

	return (
		<Box data-testid="UserInfoSelectorContainer" id="UserInfoSelectorContainer">
			<Dialog open={open} aria-labelledby="form-dialog-title">
				<DialogTitle id="form-dialog-title">{t('UserInfoSelector.title')}</DialogTitle>
				<DialogContent>
					<DialogContentText>{t('UserInfoSelector.content')}</DialogContentText>
					<Box display="flex" flexDirection="column">
						<TextField
							data-testid="userNameField"
							defaultValue={name}
							autoFocus
							margin="dense"
							id="name"
							label={t('UserInfoSelector.fields.name')}
							type="text"
							fullWidth
							inputRef={nameInput}
							onKeyPress={(event) => {
								if (event.key === 'Enter') {
									handleSave();
								}
							}}
						/>
						<Box display="flex">
							{avatars.map((avatar) => {
								return (
									<IconButton aria-label="cart">
										<StyledBadge
											badgeContent={
												<Cancel
													className={classes.close}
													onClick={() => {
														setAvatarToDelete(avatar.ID);
														setShowDeleteAvatar(true);
													}}></Cancel>
											}
											color="secondary">
											<Avatar
												className={`${classes.avatar} ${
													avatar?.ID === selectedAvatar?.ID
														? classes.selectedAvatar
														: ''
												}`}
												onClick={() => {
													if (avatar != selectedAvatar) {
														setSelectedAvatar(avatar);
													} else {
														setSelectedAvatar();
													}
												}}
												src={avatar.url}
											/>
										</StyledBadge>
									</IconButton>
								);
							})}
						</Box>
						<Dropzone onDrop={onDrop}>
							{({ getRootProps, getInputProps }) => (
								<section>
									<div {...getRootProps()}>
										<input {...getInputProps()} />
										<p>
											Drag 'n' drop some files here, or click to select files
										</p>
									</div>
								</section>
							)}
						</Dropzone>
						<Backdrop className={classes.backdrop} open={loading}>
							<CircularProgress color="inherit" />
						</Backdrop>
						<ConfirmDialog
							open={showDeleteAvatar}
							setOpen={setShowDeleteAvatar}
							title={t('UserInfoSelector.deleteAvatarDialog.title')}
							text={t('UserInfoSelector.deleteAvatarDialog.text')}
							successButtonText={t(
								'UserInfoSelector.deleteAvatarDialog.buttons.success'
							)}
							cancelButtonText={t(
								'UserInfoSelector.deleteAvatarDialog.buttons.cancel'
							)}
							onSuccess={deleteAvatarById}></ConfirmDialog>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleSave}
						color="primary"
						data-testid="rollUserInfoSelectorButton">
						{t('UserInfoSelector.buttons.save')}
					</Button>
					<Button
						onClick={() => {
							setOpen(false);
						}}
						color="primary"
						data-testid="cancelUserInfoSelectorButton">
						{t('UserInfoSelector.buttons.cancel')}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default UserInfoSelector;
